'use client';

import VideoControlBar, { Variant } from './components/jw-player/elements';
import { useEffectEvent } from './hooks/useEffectEvent';
import { useRerender } from './hooks/useRerender';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useHTML5VideoSeekDisabler from './hooks/useHTML5VideoSeekDisabler';
import { usePauseOnBackground } from './components/jw-player/use-pause-on-background';
import { useHeartbeat } from './components/jw-player/use-heartbeat';
import { useTimeout } from 'usehooks-ts';
import './vdoCipherStyle.css';
import { Box, useBreakpointValue, Image } from '@chakra-ui/react';
import { throttle } from 'lodash';
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';

type VideoQualityOption = {
  label: string;
  id?: number;
};

declare global {
  interface Window {
    VdoPlayer?: any;
  }
}


function delay(ms: number) {
  return new Promise(res => setTimeout(res, ms));
}

enum VdoCipherEvents {
  READY = 'ready',
  LOADED_METADATA = 'loadedmetadata',
  TIME_UPDATE = 'timeupdate',
  LOAD_START = 'loadstart',
  WAITING = 'waiting',
  PLAYING = 'playing',
  ENDED = 'ended',
  PAUSE = 'pause',
  PLAY = 'play',
  FULLSCREEN_CHANGE = 'fullscreenChange',
  ERROR = 'error',
}



const loadVideo = ({
  otp,
  playbackInfo,
  containerId,
  vdoCipherclassName,
  isLoading,
  videoId,
  aspectRatio = '16/9',
  iframeHeight,
  onClick,
  showVideoInFullScreen,
  showVideoInFullSize,
  wrapperOverFlowHidden,
  parentId = "",
  cleanupFunctionsRef
}) => {
  // Clear any existing iframes in the container
  const container = document.getElementById(containerId);
  if (!container) return null;

  // Remove existing iframes
  const existingIframes = container.getElementsByTagName('iframe');
  Array.from(existingIframes).forEach(iframe => iframe.remove());

  if (isLoading) return null;

  // console.log("containerIddd", containerId, 'vdoCipherclassName', vdoCipherclassName,);
  const videoWrapper = document.createElement('div');
  videoWrapper.className = vdoCipherclassName;
  videoWrapper.style.position = 'relative';
  videoWrapper.style.width = '100%';

  if(wrapperOverFlowHidden){
    videoWrapper.style.overflow = "hidden";
  }

  if (showVideoInFullScreen) {
    videoWrapper.style.height = '100vh';
    videoWrapper.style.overflow = "hidden";
  } else if(showVideoInFullSize){
    // Set wrapper to fill container completely
    videoWrapper.style.width = '100%';
    videoWrapper.style.height = '100%';
  }else {
    videoWrapper.style.aspectRatio = aspectRatio;
  }

  // Create new iframe
  const params = new URLSearchParams();
  const parametersToAdd = {
    otp,
    playbackInfo,
    controls: 'off',
  };
  for (const item in parametersToAdd) {
    params.append(item, parametersToAdd[item as keyof typeof parametersToAdd]);
  }
  const cssStyle = showVideoInFullSize
    ? `width: 100%; height: 100%; position: absolute; top: 0; left: 0;`
    : `width:100%; overflow: auto; aspect-ratio:${aspectRatio}; ${iframeHeight}`;
  const iframe = document.createElement('iframe');
  iframe.setAttribute('allowfullscreen', 'true');
  iframe.setAttribute('allow', 'autoplay; encrypted-media');
  iframe.setAttribute('frameborder', '0');
  iframe.style.cssText = cssStyle;

  iframe.src = `https://player.vdocipher.com/v2/?videoId=${videoId}&controls=off`;

  videoWrapper.appendChild(iframe);
  container.appendChild(videoWrapper);

  // Function to resize iframe to cover container
  function resizeVideoToCover() {
    if (!showVideoInFullScreen && !showVideoInFullSize) return;
    const containerWidth = container?.clientWidth || 0;
    const containerHeight = container?.clientHeight || 0;
    const [ratioW, ratioH] = aspectRatio.split('/').map(Number);
    const videoAspectRatio = ratioW / ratioH; // e.g., 16/9 = 1.777...
    if (showVideoInFullSize) {
      const containerAspectRatio = containerWidth / containerHeight;

      let scaledWidth, scaledHeight;
      if (videoAspectRatio > containerAspectRatio) {
        // Video is wider than container: scale to height, crop width
        scaledHeight = containerWidth * videoAspectRatio;
        scaledWidth = containerHeight * videoAspectRatio;
      } else {
        // Video is taller than container: scale to width, crop height
        scaledWidth = containerWidth;
        scaledHeight = containerWidth / videoAspectRatio;
      }

      // Apply scaled size and center the iframe
      iframe.style.width = scaledWidth + 'px';
      iframe.style.height = scaledHeight + 'px';
      iframe.style.position = 'absolute';
      iframe.style.top = '50%';
      iframe.style.left = '50%';
      iframe.style.transform = 'translate(-50%, -50%)';
    } else {
      // Calculate scaled dimensions to cover (max factor)
      let scaledWidth = containerWidth;
      let scaledHeight = containerWidth / videoAspectRatio;

      if (scaledHeight < containerHeight) {
        // Scale to height if width-based height is too small
        scaledHeight = containerHeight;
        scaledWidth = containerHeight * videoAspectRatio;
      }

      // Apply scaled size
      iframe.style.width = scaledWidth + 'px';
      iframe.style.height = scaledHeight + 'px';
    }
  }

  if (showVideoInFullScreen || showVideoInFullSize) {

    // Store handlers for cleanup
    const loadHandler = () => resizeVideoToCover();
    
    // Run resize on load and window resize
    iframe.addEventListener('load', loadHandler);
    window.addEventListener('resize', loadHandler);

    if(parentId){
      const idElement = document.querySelector(parentId);
      if (idElement) {

        const observer = new ResizeObserver(resizeVideoToCover);
        observer.observe(idElement);
        
        // Store cleanup functions in the ref
        cleanupFunctionsRef.current = {
          loadHandler,
          resizeObserver: observer,
        };
      } else {
        cleanupFunctionsRef.current = {
          loadHandler,
        };
      }
    } else {
      cleanupFunctionsRef.current = {
        loadHandler,
      };
    }
    

    // Initial resize
    resizeVideoToCover();
  }

  return iframe;
};

export function VdoCipherPlayer({
  id,
  otp,
  playbackInfo,
  variant,
  isRewardsAudio,
  startTime,
  webType,
  toggleAlternateStream,
  isAutoPlayEnabled,
  onAutoPlayDismiss,
  onAutoPlayNextVideo,
  setVideoQuality,
  videoTitle,
  videoType,
  getStartTime,
  onVideoProgress,
  onComplete,
  onLoad,
  onError: onErrorProp,
  onReady: onReadyProp,
  autoPlay = false,
  onHeartbeat,
  isLoading = false,
  playerInstanceRef: playerRefProp,
  englishTitle,
  usedScreen,
  aspectRatio,
  videoId,
  saveVideoTime = false,
  startVideo = false,
  hideFullScreen = false,
  hideSoundIcon = false,
  hideDragProgBar = false,
  videoControllerForShorts = undefined,
  muteStatus = false,
  hidePlayPauseIcon = false,
  iframeHeight = '',
  shortsVideo = false,
  onClick,
  loopVideo = false,
  onMuteStatusChange,
  videoData,
  videoIndx,
  setVideoIndx,
  navArrowBarW,
  controlAutoPlay,
  startVideoInUseEffect,
  mixPanelTrigger = () => { },
  showVideoInFullScreen = false,
  showVideoInFullSize = false,
  wrapperOverFlowHidden = false,
  fallbackImageAlt,
  fallbackImageSrc,
  showFallbackImage = false,
  fallbackImageStyle = {},
  fallbackImageStyleProps = {},
  isSadhguruBgVideo,
  videoQualityId = undefined,
  parentId = ""
}) {

  const prefixedId = id;
  const containerId = `${prefixedId}-container`;
  const vdoCipherclassName = `${prefixedId}-vdoWrap`;

  let [scriptLoaded, setScriptLoaded] = useState(() => typeof window !== 'undefined' && !!window.VdoPlayer)
  const [isComplete, setIsComplete] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [buttonFaded, setButtonFaded] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [qualityLevels, setQualityLevels] = useState<
    VideoQualityOption[] | undefined
  >(undefined);
  const [bufferedPercentage, setBufferedPercentage] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [volume, setVolume] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedVolume = localStorage.getItem('player-volume');
      return savedVolume ? parseInt(savedVolume, 10) : 100;
    }
    return 100;
  });
  const playerInstanceRef = useRef<any>(null);
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const lastReportedVideoPosition = useRef<number | null>(null);
  const cleanupFunctionsRef = useRef<{
    loadHandler?: () => void;
    resizeObserver?: ResizeObserver;
  }>({});
  const muteRef = useRef({
    isMuted: false,
    volume: 100,
  });

  const rerender = useRerender();
  const onReady = useEffectEvent(onReadyProp);
  const onError = useEffectEvent(onErrorProp);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const script = document.createElement('script');
    script.src = "https://player.vdocipher.com/v2/api.js";
    script.async = true;

    script.onload = () => {
      console.log("VdoCipher script loaded");
      setScriptLoaded(true);
      rerender()
    };

    document.body.appendChild(script);
  }, []);

  // Cleanup event listeners and observers on component unmount
  useEffect(() => {
    return () => {
      const cleanup = cleanupFunctionsRef.current;
      if (cleanup.loadHandler) {
        // Find the iframe and remove the load listener
        const iframes = document.querySelectorAll('iframe');
        iframes.forEach(iframe => {
          iframe.removeEventListener('load', cleanup.loadHandler!);
        });
      }
      if (cleanup.resizeObserver) {
        cleanup.resizeObserver.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (controlAutoPlay && playerInstanceRef?.current?.video && isLoaded && scriptLoaded) {
      if (startVideoInUseEffect) {
        playerInstanceRef.current?.video?.play();
      }
      else {
        playerInstanceRef.current?.video?.pause();
      }
      if (muteStatus && !muteRef.current?.isMuted) {
        muteRef.current.isMuted = true;
        muteRef.current.volume = volume;
        setVolumeInPlayer(0);
      }
      else if (!muteStatus && muteRef.current.isMuted) {
        muteRef.current.isMuted = false;
        setVolumeInPlayer(muteRef.current.volume || 100);
      }
    }
  }, [controlAutoPlay, startVideoInUseEffect, isLoaded, scriptLoaded])

  const isHomeWorkAudio = variant === 'home-work-audio';
  const isLivePlayer = variant === 'live-video';

  const isMuted = muteRef.current.isMuted;

  const onTime = useEffectEvent(() => {
    if (playerInstanceRef.current?.video) {
      const duration = playerInstanceRef.current.video.duration;
      const position = playerInstanceRef.current.video.currentTime;
      setVideoDuration(duration);
      setCurrentTime(position);
      if (!isAutoPlayEnabled)
        saveVideoTime && updateVideoTime(videoId, position);

      const percentage = +(position / duration).toFixed(2);

      if (lastReportedVideoPosition.current !== position) {
        lastReportedVideoPosition.current = position;

        if (variant !== 'live-video' || webType === 'RECORDED_LIVE') {
          playerInstanceRef.current?.api
            ?.getTotalCovered()
            .then((totalCovered: number) => {
              const newBufferedPercentage = (totalCovered / duration) * 100;
              setBufferedPercentage(Math.round(newBufferedPercentage));
            })
            .catch(() => { });

          onVideoProgress &&
            onVideoProgress({
              percentage,
              position,
              timeLeft: duration - position,
              duration,
            });
        }
      }
    }
  });

  const setUpPlayer = useEffectEvent(
    ({
      videoId,
      otpProp,
      playbackInfoProp,
      iframeHeight
    }) => {
      if (!window.VdoPlayer) {
        console.error('VdoPlayer SDK not loaded');
        return;
      }
      const handleError = (error: any) => {
        // console.error('VdoCipher Player Error:', error);
        if (isLivePlayer) {
          if (playerInstanceRef.current) {
            playerInstanceRef.current.destroy?.();
            playerInstanceRef.current = null;
          }

          const container = document.getElementById(containerId);
          if (container) {
            container.innerHTML = '';
          }
          onErrorProp?.();
        } else {
          // alert("PlayerError")
        }
      };

      const iframe = loadVideo({
        otp: otpProp,
        playbackInfo: playbackInfoProp,
        containerId,
        vdoCipherclassName,
        isLoading,
        aspectRatio,
        videoId,
        iframeHeight,
        onClick,
        showVideoInFullScreen,
        showVideoInFullSize,
        wrapperOverFlowHidden,
        parentId,
        cleanupFunctionsRef
      });
      if (iframe && !isLoading) {
        try {
          const player = new window.VdoPlayer(iframe);

          player.video.addEventListener(VdoCipherEvents.PLAY, () => {
            setIsPlaying(true)
            if (!isPlaying && mixPanelTrigger) {
              mixPanelTrigger()
            }
          }
          );
          player.video.addEventListener(VdoCipherEvents.PAUSE, () => {
            setIsPlaying(false)
          }
          );

          player.video.addEventListener(VdoCipherEvents.READY, () => {
            // console.log('player is ready');
          });

          player.video.addEventListener(
            VdoCipherEvents.LOADED_METADATA,
            async () => {
              setIsBuffering(false);
              playerInstanceRef.current = player;
              setIsLoaded(true);
              setIsReady(true);

              await player.api.hideCaptions();
              const vdoPlayTimeObjStr = localStorage.getItem('vdo-player-time');
              const videoPlayTimeObj = vdoPlayTimeObjStr
                ? JSON.parse(vdoPlayTimeObjStr)
                : {};
              const savedTime = videoPlayTimeObj[videoId || ''];
              const timeToSeek = parseInt(savedTime) || 0;
              if (startTime || timeToSeek) {
                player.video.currentTime = Math.max(startTime ?? 0, timeToSeek);
              }

              setVideoDuration(player.video.duration);

              // Store the player instance in the ref
              if (playerRefProp) {
                playerRefProp.current = player;
              }

              onLoad && onLoad();
              onReady && onReady();

              if(videoQualityId){
                try {
                  await playerInstanceRef.current.api.setVideoQuality(videoQualityId/1)             
                } catch (error) {
                  console.log("errorInSetVideoQuality", error);                 
                }
              }

              if (startVideo) {
                seekVideo(0)
                playerInstanceRef.current?.video?.play();
              }

              if (autoPlay) {
                player.video.play();
              }
            },
          );

          player.video.addEventListener(VdoCipherEvents.LOAD_START, () => {
            setIsBuffering(true);
          });

          player.video.addEventListener(VdoCipherEvents.WAITING, () => {
            setIsBuffering(true);
          });

          player.video.addEventListener(VdoCipherEvents.PLAYING, () => {
            setIsBuffering(false);
            // clearTimeout(playbackTimeout);
          });

          player.video.addEventListener(VdoCipherEvents.TIME_UPDATE, () => {
            onTime();
          });
          player.video.addEventListener(VdoCipherEvents.ENDED, () => {
            setIsComplete(true);
            if (saveVideoTime) updateVideoTime(videoId, 0);
            onComplete && onComplete();
            if (loopVideo) {
              seekVideo(0)
              playerInstanceRef.current?.video?.play();
            }
          });

          player.api.addEventListener(VdoCipherEvents.FULLSCREEN_CHANGE, () => {
            // console.log('fullscreenChanged');
            setIsFullScreen(!isFullScreen);
          });
          player.video.addEventListener(VdoCipherEvents.ERROR, (event: any) => {
            handleError(event);
          });
        } catch (error) {
          // console.error('Error initializing VdoPlayer:', error);
          handleError(error);
        }
      }
    },
  );

  useHeartbeat({
    onHeartbeat,
    enabled: isLoaded && !isComplete && !!onHeartbeat,
    getTime: () => playerInstanceRef.current?.video?.currentTime || 0,
  });

  const { adjustVideoTime, startStopSeekDisable } = useHTML5VideoSeekDisabler(
    playerContainerRef,
    startTime || 0,
    webType !== 'JW_LIVE',
  );

  useLayoutEffect(() => {
    if (scriptLoaded && !isLoading) {
      const playbackDetails = {
        videoId,
        otpProp: otp,
        playbackInfoProp: playbackInfo,
        iframeHeight: iframeHeight
      };
      setUpPlayer(playbackDetails);
    }

    return () => {
      (async () => {
        // console.log("icamedheretodestroy", videoId);
        if (scriptLoaded && !isLoading) {
          setIsLoaded(false);

          const waitForPlayerLoad = async () => {
            let time = 0;
            // Waits for the player to load for 5 sec and returns true/false
            while (time <= 5000 && !playerInstanceRef.current) {
              time += 100;
              await delay(100);
            }
            return Boolean(playerInstanceRef.current);
          };

          const isPlayerLoaded = await waitForPlayerLoad();
          if (isPlayerLoaded) {
            playerInstanceRef.current?.destroy?.();
            playerInstanceRef.current = null;
            if (playerRefProp) {
              playerRefProp.current = null;
            }

            const container = document.getElementById(containerId);
            if (container) {
              container.innerHTML = '';
            }
          }
        }
      })();
    };
  }, [
    scriptLoaded,
    isLoading,
    otp,
    playbackInfo,
    playerInstanceRef,
    setUpPlayer,
  ]);

  // Create throttled function ref that persists across renders
  const throttledUpdateRef = useRef(
    throttle((id: string, t: number) => {
      const stored = localStorage.getItem('vdo-player-time');
      const obj = stored ? JSON.parse(stored) : {};
      obj[id] = t;
      localStorage.setItem('vdo-player-time', JSON.stringify(obj));
    }, 1000),
  );

  const updateVideoTime = useEffectEvent(
    (videoId: string | undefined, time: number) => {
      if (!videoId) return;
      throttledUpdateRef.current(videoId, time);
    },
  );

  // Seek to a specific time
  const seekVideo = useEffectEvent((time: number, isRerender = true) => {
    startStopSeekDisable(true);
    try {
      adjustVideoTime(time);
      if (playerInstanceRef.current?.video) {
        playerInstanceRef.current.video.currentTime = time;
      }
    } catch (e) {
      console.error('Error seeking video:', e);
    }
    startStopSeekDisable(false);
    if (isRerender) rerender();
  });

  // For those videos which the user have to watch again after completion
  const handlePlayAfterComplete = useEffectEvent(() => {
    seekVideo(0);
    if (isComplete) {
      setIsComplete(false);
      playerInstanceRef.current?.video?.play();
    } else {
      togglePlayOrPause();
    }
  });

  const togglePlayOrPause = useEffectEvent(() => {
    setButtonFaded(false);
    if (isRewardsAudio && isComplete) {
      handlePlayAfterComplete();
    } else {
      if (isPlaying) {
        playerInstanceRef.current?.video?.pause();
      } else {
        playerInstanceRef.current?.video?.play();
      }
    }
    rerender();
  });

  const setVolumeInPlayer = useEffectEvent((newVolume: number) => {
    if (playerInstanceRef.current?.video) {
      playerInstanceRef.current.video.volume = newVolume / 100;
      setVolume(newVolume);

      if (typeof window !== 'undefined') {
        localStorage.setItem('player-volume', newVolume.toString());
      }

      if (newVolume === 0) {
        playerInstanceRef.current.video.muted = true;
        muteRef.current.isMuted = true;
      } else {
        playerInstanceRef.current.video.muted = false;
        muteRef.current.isMuted = false;
      }

      rerender();
    }
  });

  useEffect(() => {
    setVolumeInPlayer(volume);
  }, [volume, setVolumeInPlayer]);

  // console.log("muteRef=========>", muteRef.current);


  const toggleMute = useEffectEvent(() => {
    if (muteRef.current.isMuted) {
      muteRef.current.isMuted = false;
      setVolumeInPlayer(muteRef.current.volume || 100);
      if (onMuteStatusChange) {
        onMuteStatusChange(false)
      }
    } else {
      muteRef.current.isMuted = true;
      muteRef.current.volume = volume;
      setVolumeInPlayer(0);
      if (onMuteStatusChange) {
        onMuteStatusChange(true)
      }
    }
    rerender();
  });

  const handleRewind = useEffectEvent(() => {
    if (playerInstanceRef.current?.video) {
      const time = playerInstanceRef.current.video.currentTime;
      seekVideo(Math.max(0, time - 30));
      if (!isPlaying) {
        togglePlayOrPause();
      }
    }
  });

  const handleForward = useEffectEvent(() => {
    if (playerInstanceRef.current?.video) {
      const length = playerInstanceRef.current.video.duration;
      seekVideo(Math.min(length - 10));
      if (!isPlaying) {
        togglePlayOrPause();
      }
    }
  });

  const toggleFullScreen = useEffectEvent(async () => {
    try {
      const domElement = document.getElementsByClassName(vdoCipherclassName)[0];
      const uiDiv = document.getElementById(`${prefixedId}-ui`);
      const container = document.getElementById(containerId);
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      if (isFullScreen) {
        // EXIT FULLSCREEN
        if (isIOS) {
          // Restore scroll position synchronously
          const prevScrollY = parseInt(
            document.body.dataset.scrollY || '0',
            10
          );

          // Temporarily disable scroll restoration jump
          document.body.style.transition = 'none';
          document.documentElement.style.scrollBehavior = 'auto';

          // Remove fixed styles but keep scroll locked until restored
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.overflow = '';
          document.body.style.width = '';
          document.body.style.height = '';
          document.documentElement.style.overflow = '';

          // Immediate restore before paint
          requestAnimationFrame(() => {
            window.scrollTo(0, prevScrollY);
            document.body.style.transition = '';
            document.documentElement.style.scrollBehavior = '';
          });

          // Reset video container styles
          if (domElement) {
            (domElement as HTMLElement).style.cssText = '';
          }

          // Remove overlay
          const overlay = document.getElementById('fullscreen-overlay');
          if (overlay) {
            document.body.removeChild(overlay);
          }
        } else {
          if (document.fullscreenElement) {
            await document.exitFullscreen();
          }
        }

        setIsFullScreen(false);
        if (uiDiv && container) container.appendChild(uiDiv);
      } else {
        // ENTER FULLSCREEN
        if (isIOS) {
          const scrollY = window.scrollY;
          document.body.dataset.scrollY = scrollY.toString();

          // Lock the scroll at current position
          document.body.style.position = 'fixed';
          document.body.style.top = `-${scrollY}px`;
          document.body.style.overflow = 'hidden';
          document.body.style.width = '100%';
          document.body.style.height = '100%';
          document.documentElement.style.overflow = 'hidden';

          // Add fullscreen overlay
          const overlay = document.createElement('div');
          overlay.id = 'fullscreen-overlay';
          overlay.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100%;
          background-color: #000000;
          z-index: 9998;
        `;
          document.body.appendChild(overlay);

          // Expand video
          if (domElement) {
            (domElement as HTMLElement).style.cssText = `
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100vw !important;
            height: 100% !important;
            z-index: 9999 !important;
            background-color: #000000 !important;
          `;
          }

          setIsFullScreen(true);
          if (uiDiv) domElement?.appendChild(uiDiv);
        } else {
          // Desktop fullscreen
          if (domElement?.requestFullscreen) {
            await domElement.requestFullscreen();
            // Add black background for desktop too
            (domElement as HTMLElement).style.backgroundColor = '#000000';
            setIsFullScreen(true);
            if (uiDiv) domElement.appendChild(uiDiv);
          }
        }
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
    rerender();
  });

  // Clean up on component unmount
  useEffect(() => {
    return () => {
      // Clean up if component unmounts while in fullscreen
      const overlay = document.getElementById('fullscreen-overlay');
      if (overlay) {
        document.body.removeChild(overlay);
      }
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) return;

    const iframe = container.querySelector('iframe');
    if (iframe) {
      if (isFullScreen) {
        iframe.classList.add('vdo-fullscreen');
      } else {
        iframe.classList.remove('vdo-fullscreen');
      }
    }
  }, [isFullScreen, containerId]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isDocumentInFullscreen = Boolean(document.fullscreenElement);
      if (isFullScreen !== isDocumentInFullscreen) {
        setIsFullScreen(isDocumentInFullscreen);
        rerender();
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [isFullScreen, rerender]);

  // Get video quality options
  const getVideoQuality = useEffectEvent(async () => {
    if (playerInstanceRef.current?.api) {
      try {
        const videoQualityData =
          await playerInstanceRef.current.api.getVideoQualities();
        // if(id?.includes('TopBanner')){
        //   console.log("TOPBANNERRRRRRRRRRRRRRRRR",id, videoQualityData);
        // }
        if (
          videoQualityData?.qualities &&
          videoQualityData.qualities.length > 0
        ) {
          const groupedQualities: VideoQualityOption[] = [];
          groupedQualities.push({
            label: 'Auto',
            id: 0,
          });
          videoQualityData.qualities.forEach(
            (quality: { label: string; height: number; id: number }) => {
              if (quality?.label?.includes(' kbps')) {
                const kbpsStr = quality.label.split(' kbps')[0];
                const kbps = parseInt(kbpsStr || '0');

                if (!isNaN(kbps)) {
                  let formattedLabel: string;
                  if (kbps >= 1000) {
                    const mbps = kbps / 1000;
                    const roundedMbps = Math.round(mbps);
                    formattedLabel = `${roundedMbps} Mbps`;
                  } else {
                    const roundedKbps = Math.round(kbps / 50) * 50;
                    formattedLabel = `${roundedKbps} Kbps`;
                  }

                  groupedQualities.push({
                    label: formattedLabel,
                    id: quality.id + 1,
                  });
                } else {
                  groupedQualities.push({
                    label: quality.label,
                    id: quality.id + 1,
                  });
                }
              } else {
                groupedQualities.push({
                  label: quality.label,
                  id: quality.id + 1,
                });
              }
            },
          );
          setQualityLevels(groupedQualities);
        }
      } catch (error) {
        // console.error('Error getting video qualities:', error);
      }
    }
  });

  useEffect(() => {
    if (isReady) {
      getVideoQuality();
    }
  }, [isReady, getVideoQuality]);

  const [currVidQuality, setCurrVidQuality] = useState(0);
  const setQualityLevel = useEffectEvent(
    async (quality?: number, id?: number) => {
      if (playerInstanceRef.current?.api && id !== undefined) {
        try {
          if (id === 0) {
            await playerInstanceRef.current.api.enableAdaptiveVideo();
            setCurrVidQuality(0);
          } else {
            await playerInstanceRef.current.api.setVideoQuality(id - 1);
            setCurrVidQuality(quality || 0);
          }
          rerender();
        } catch (error) {
          // console.error('Error setting video quality:', error);
        }
      }
    },
  );

  // Update external video quality state if needed
  useEffect(() => {
    if (setVideoQuality && qualityLevels && qualityLevels.length > 0) {
      const qualityLevel = qualityLevels[currVidQuality]?.label || '';
      setVideoQuality(qualityLevel);
    }
  }, [currVidQuality, qualityLevels, setVideoQuality]);

  // Fade out controls after 3 seconds of inactivity
  useTimeout(
    () => {
      if(!isSadhguruBgVideo){
        setButtonFaded(true);
      }
    },
    isPlaying && !isHomeWorkAudio && !buttonFaded ? 3000 : null,
  );

  // Pause video when app goes to background
  usePauseOnBackground({
    enabled: isLoaded && variant === 'live-video',
    isPlaying,
    togglePlay: togglePlayOrPause,
    getStartTime,
    seekVideo,
  });
  let soundIconSize = useBreakpointValue({ base: '40px', '2xl': '52px' })
  let playPauseBtnSize = useBreakpointValue({ base: '80px', md: '90px', '2xl': '120px' })

  useEffect(() => {
    const videoControlBar = document.getElementById(`${id}-ui`);
    if (videoControlBar) {
      videoControlBar.addEventListener('mouseenter', () => {
        setButtonFaded(false);
      });
      videoControlBar.addEventListener('mousemove', () => {
        setButtonFaded(false);
      });
    }
  }, []);


  let nextPrevIconSize = useBreakpointValue({ base: '44px', md: 'auto' })
  let svgNextPrevIconColour = useBreakpointValue({ base: { fill: '#00000099', stroke: '#fff' }, md: { fill: '#FFFFFF99', stroke: '#1D1A16' } })
  let shortsParentController = useBreakpointValue({base: () => {setButtonFaded(false)}, xl: togglePlayOrPause})

  
  return (
    <Box
      onClick={hidePlayPauseIcon ? () => {} : togglePlayOrPause}
      position={'relative'}
      width={'100%'}
      height={'100%'}
      id={containerId}
      ref={playerContainerRef}>

      {/* This div is the stable portal target */}
      {videoControllerForShorts ? null :
        <div id={`${prefixedId}-ui`} style={{ position: 'absolute', inset: 0, zIndex: 1, backgroundColor: '' }} />}

      {!isLoaded &&
        (showFallbackImage ? (
          <Box position={"absolute"} top={0} left={0}>
          <LazyLoadImageComponent
            style={fallbackImageStyle}
            src={fallbackImageSrc}
            alt={fallbackImageAlt}
            {...fallbackImageStyleProps}
          />
        </Box>
        ) : (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'black',
              zIndex: 50,
              width: '100%',
              height: '100%',
            }}
            className="flex items-center justify-center"
          >
            <div className="w-7 animate-spin-slow">
              <svg
                style={{ width: '25px', height: '25px' }}
                viewBox="0 0 47 47"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M23.5009 3.91699C24.5825 3.91699 25.4592 4.79377 25.4592 5.87533V11.7503C25.4592 12.8319 24.5825 13.7087 23.5009 13.7087C22.4193 13.7087 21.5426 12.8319 21.5426 11.7503V5.87533C21.5426 4.79377 22.4193 3.91699 23.5009 3.91699ZM37.3484 9.65282C38.1132 10.4176 38.1132 11.6575 37.3484 12.4223L33.1942 16.5766C32.4294 17.3413 31.1894 17.3413 30.4247 16.5766C29.6599 15.8118 29.6599 14.5718 30.4247 13.8071L34.5789 9.65282C35.3437 8.88804 36.5836 8.88804 37.3484 9.65282ZM9.6534 9.65282C10.4182 8.88804 11.6581 8.88804 12.4229 9.65282L16.5772 13.8071C17.3419 14.5718 17.3419 15.8118 16.5772 16.5766C15.8124 17.3413 14.5724 17.3413 13.8077 16.5766L9.6534 12.4223C8.88862 11.6575 8.88862 10.4176 9.6534 9.65282ZM3.91699 23.5017C3.91699 22.4201 4.79377 21.5433 5.87533 21.5433H11.7503C12.8319 21.5433 13.7087 22.4201 13.7087 23.5017C13.7087 24.5832 12.8319 25.46 11.7503 25.46H5.87533C4.79377 25.46 3.91699 24.5832 3.91699 23.5017ZM33.292 23.5017C33.292 22.4201 34.1688 21.5433 35.2503 21.5433H41.1253C42.2069 21.5433 43.0837 22.4201 43.0837 23.5017C43.0837 24.5832 42.2069 25.46 41.1253 25.46H35.2503C34.1688 25.46 33.292 24.5832 33.292 23.5017ZM16.5772 30.4241C17.3419 31.1889 17.3419 32.4288 16.5772 33.1936L12.4229 37.3478C11.6581 38.1126 10.4182 38.1126 9.6534 37.3478C8.88862 36.5831 8.88862 35.3431 9.6534 34.5783L13.8077 30.4241C14.5724 29.6593 15.8124 29.6593 16.5772 30.4241ZM30.4247 30.4241C31.1894 29.6593 32.4294 29.6593 33.1942 30.4241L37.3484 34.5783C38.1132 35.3431 38.1132 36.5831 37.3484 37.3478C36.5836 38.1126 35.3437 38.1126 34.5789 37.3478L30.4247 33.1936C29.6599 32.4288 29.6599 31.1889 30.4247 30.4241ZM23.5009 33.292C24.5825 33.292 25.4592 34.1688 25.4592 35.2503V41.1253C25.4592 42.2069 24.5825 43.0837 23.5009 43.0837C22.4193 43.0837 21.5426 42.2069 21.5426 41.1253V35.2503C21.5426 34.1688 22.4193 33.292 23.5009 33.292Z"
                  fill="#CE6113"
                />
              </svg>
            </div>
          </div>
        ))}
      {videoControllerForShorts ?
        <Box
          id={`${prefixedId}-ui`}
          onClick={shortsParentController}
          onMouseEnter={() => {setButtonFaded(false)}}
          position={'fixed'} height={'98vh'}
          top={'0rem'}
          width={videoControllerForShorts} display={'flex'} alignItems={'center'}
          mx={'auto'} zIndex={10}
         >
          <Box
            mx={{ base: 'auto', md: '-5rem' }}  style={{
            opacity: buttonFaded ? 0 : 1,
          }} mb={{ base: '0px', lg: '2rem' }} width={navArrowBarW} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
            <svg
              onClick={(e) => {
                e.stopPropagation()
                if (videoIndx > 0) {
                    setVideoIndx(videoIndx - 1)
                }
                else {
                  setVideoIndx(videoData?.length - 1)
                }
              }}
              className='prevArrowSvg'
              style={{ zIndex: 11, cursor: 'pointer', width: nextPrevIconSize, height: nextPrevIconSize }} width="64" height="64" viewBox="0 0 64 64" fill={svgNextPrevIconColour?.stroke} xmlns="http://www.w3.org/2000/svg">
              <rect width="64" height="64" rx="32" fill={svgNextPrevIconColour?.fill} fill-opacity="0.6" />
              <path fill-rule="evenodd" clip-rule="evenodd" d="M36.9435 20.3905C36.4228 19.8698 35.5785 19.8698 35.0578 20.3905L24.3912 31.0572C24.1411 31.3072 24.0007 31.6464 24.0007 32C24.0007 32.3536 24.1411 32.6927 24.3912 32.9428L35.0578 43.6095C35.5785 44.1302 36.4228 44.1302 36.9435 43.6095C37.4642 43.0888 37.4642 42.2446 36.9435 41.7239L27.2196 32L36.9435 22.2761C37.4642 21.7554 37.4642 20.9112 36.9435 20.3905Z" fill={svgNextPrevIconColour?.stroke} />
            </svg>
            {isPlaying ? (
              <Image width={playPauseBtnSize} height={playPauseBtnSize} cursor={'pointer'}
                onClick={e => {e.stopPropagation(); togglePlayOrPause()}}
                src={'https://static.sadhguru.org/d/46272/1758777174-pauseiconvdocipher.png'} alt='pause' 
                sx={{
                      '@media (hover: hover)': {
                        '&:hover': {
                          content: 'url(https://static.sadhguru.org/d/46272/1761721429-pausehover.svg)'
                        }
                      },
                      '@media (hover: none)': {
                          '&:active': {
                          content: 'url(https://static.sadhguru.org/d/46272/1761721429-pausehover.svg)'
                          }
                        }
                    }}
                />
            ) : (
              <Image width={playPauseBtnSize} height={playPauseBtnSize} cursor={'pointer'}
                onClick={e => {e.stopPropagation(); togglePlayOrPause()}} src={'https://static.sadhguru.org/d/46272/1758777174-playiconvdocipher.png'} alt='play'
                sx={{
                      '@media (hover: hover)': {
                        '&:hover': {
                          content: 'url(https://static.sadhguru.org/d/46272/1761721429-playhover.svg)'
                        }
                      },
                      '@media (hover: none)': {
                          '&:active': {
                          content: 'url(https://static.sadhguru.org/d/46272/1761721429-playhover.svg)'
                          }
                        }
                    }}
                />
            )}
            <svg
              onClick={(e) => {
                e.stopPropagation()
                if (videoIndx/1 < (videoData.length - 1)/1) {
                    setVideoIndx(videoIndx + 1)                  
                }
                else {
                  setVideoIndx(0)
                }
              }}
              className='nextArrowSvg'
              style={{
                zIndex: 11,
                cursor: 'pointer', width: nextPrevIconSize, height: nextPrevIconSize, transform: 'rotate(180deg)'
              }} width="64" height="64" viewBox="0 0 64 64" fill={svgNextPrevIconColour?.stroke} xmlns="http://www.w3.org/2000/svg">
              <rect width="64" height="64" rx="32" fill={svgNextPrevIconColour?.fill} fill-opacity="0.6" />
              <path fill-rule="evenodd" clip-rule="evenodd" d="M36.9435 20.3905C36.4228 19.8698 35.5785 19.8698 35.0578 20.3905L24.3912 31.0572C24.1411 31.3072 24.0007 31.6464 24.0007 32C24.0007 32.3536 24.1411 32.6927 24.3912 32.9428L35.0578 43.6095C35.5785 44.1302 36.4228 44.1302 36.9435 43.6095C37.4642 43.0888 37.4642 42.2446 36.9435 41.7239L27.2196 32L36.9435 22.2761C37.4642 21.7554 37.4642 20.9112 36.9435 20.3905Z" fill={svgNextPrevIconColour?.stroke} />
            </svg>
          </Box>
          <style>
          {`
            /* Desktop hover effect */
            @media (hover: hover) {
              .prevArrowSvg:hover rect,
              .nextArrowSvg:hover rect {
                fill: #FFFFFF;
              }
            }
            @media (hover: hover) and (max-width: 500px) {
              .prevArrowSvg:hover rect,
              .nextArrowSvg:hover rect {
                fill: #1D1A16;
              }
            }
          `}
        </style>

          <Box position={'absolute'} right={'1rem'}
            bottom={{ base: '5rem', md: '1rem' }}>
            {isMuted ?
              <Image width={soundIconSize} height={soundIconSize} onClick={(e) => {
                e.stopPropagation()
                toggleMute()
              }} src={'https://static.sadhguru.org/d/46272/1758777174-muteiconvdocipher.png'} alt='mute' 
              sx={{
                      '@media (hover: hover)': {
                        '&:hover': {
                          background: "#FFFFFF33",
                          borderRadius: '100%'
                        }
                      },
                      '@media (hover: none)': {
                        '&:active': {
                          background: "#FFFFFF33",
                          borderRadius: '100%'
                        }
                      }
                    }}
              /> :
              <Image width={soundIconSize} height={soundIconSize} onClick={(e) => {
                e.stopPropagation()
                toggleMute()
              }} src={'https://static.sadhguru.org/d/46272/1758777174-soundiconvdocipher.png'} alt='volume'
              sx={{
                      '@media (hover: hover)': {
                        '&:hover': {
                          background: "#FFFFFF33",
                          borderRadius: '100%'
                        }
                      },
                      '@media (hover: none)': {
                        '&:active': {
                          background: "#FFFFFF33",
                          borderRadius: '100%'
                        }
                      }
                    }} />}
          </Box>
        </Box>
        : null}

      {isLoaded && !videoControllerForShorts && document.getElementById(`${prefixedId}-ui`)
        ? createPortal(
          <>
            <VideoControlBar
              {...{
                isAutoPlayEnabled,
                onAutoPlayDismiss,
                isPlaying,
                isMuted,
                isFullScreen,
                volume,
                duration: videoDuration,
                bufferedPercentage,
                currentTime,
                setVolume,
                toggleMute,
                toggleFullScreen,
                selectVideoQuality: setQualityLevel,
                videoQualityOptions: qualityLevels,
                selectedVideoQuality: currVidQuality,
                handleRewind,
                handleForward,
                isBuffering,
                seekVideo,
                buttonFaded,
                setButtonFaded,
                handlePlayOrPause: togglePlayOrPause,
                playerInstanceRef,
                onAutoPlayNextVideo,
                variant,
                videoTitle,
                videoType,
                englishTitle,
                hideFullScreen,
                hideSoundIcon,
                hideDragProgBar,
                hidePlayPauseIcon,
                onClick,
                shortsVideo,
                id,
                isSadhguruBgVideo
              }}
            />
          </>,
          document.getElementById(`${prefixedId}-ui`)!
        )
        : null}
    </Box>
  );
}
